<div class="toolbar">
  <button
    type="button"
    mat-icon-button
    [matTooltip]="pasteTooltip()"
    aria-label="Paste JSON from clipboard"
    i18n-aria-label="@@toolbar.paste.aria"
    [disabled]="pasteDisabled()"
    [class.paste-ready]="clipboardState() === 'enabled-json'"
    (click)="onPasteClick()"
  >
    <jj-icon name="paste" />
  </button>

  <button
    type="button"
    mat-icon-button
    matTooltip="Copy editor contents to clipboard (hold Alt to copy as escaped JSON string)"
    i18n-matTooltip="@@toolbar.copy.tooltip"
    aria-label="Copy editor contents to clipboard"
    i18n-aria-label="@@toolbar.copy.aria"
    [disabled]="!hasContent()"
    (click)="onCopyClick($event)"
  >
    <jj-icon name="copy" />
  </button>

  <button
    type="button"
    mat-icon-button
    matTooltip="Upload file"
    i18n-matTooltip="@@toolbar.upload.tooltip"
    aria-label="Upload file"
    i18n-aria-label="@@toolbar.upload.aria"
    (click)="triggerFilePicker()"
  >
    <jj-icon name="upload" />
  </button>
  <input
    #fileInput
    type="file"
    class="hidden-file-input"
    accept=".json,.jsonc,.jsonl,.geojson,.txt,application/json"
    (change)="onFileChange($event)"
  />

  <button
    type="button"
    mat-icon-button
    matTooltip="Download as file"
    i18n-matTooltip="@@toolbar.download.tooltip"
    aria-label="Download as file"
    i18n-aria-label="@@toolbar.download.aria"
    [disabled]="!hasContent()"
    (click)="onDownloadClick()"
  >
    <jj-icon name="download" />
  </button>

  <span class="divider" aria-hidden="true"></span>

  <button
    type="button"
    mat-icon-button
    matTooltip="Format (pretty-print)"
    i18n-matTooltip="@@toolbar.format.tooltip"
    aria-label="Format"
    i18n-aria-label="@@toolbar.format.aria"
    [disabled]="!hasContent()"
    (click)="onFormatClick()"
  >
    <jj-icon name="format" />
  </button>

  <button
    type="button"
    mat-icon-button
    matTooltip="Minify"
    i18n-matTooltip="@@toolbar.minify.tooltip"
    aria-label="Minify"
    i18n-aria-label="@@toolbar.minify.aria"
    [disabled]="!hasContent()"
    (click)="onMinifyClick()"
  >
    <jj-icon name="minify" />
  </button>

  <button
    type="button"
    mat-icon-button
    matTooltip="Sort keys"
    i18n-matTooltip="@@toolbar.sort.tooltip"
    aria-label="Sort keys"
    i18n-aria-label="@@toolbar.sort.aria"
    [disabled]="!hasContent()"
    (click)="onSortClick()"
  >
    <jj-icon name="sort" />
  </button>

  <button
    type="button"
    mat-icon-button
    matTooltip="Clear editor"
    i18n-matTooltip="@@toolbar.clear.tooltip"
    aria-label="Clear editor"
    i18n-aria-label="@@toolbar.clear.aria"
    [disabled]="!hasContent()"
    (click)="onClearClick()"
  >
    <jj-icon name="clear" />
  </button>

  <span class="divider" aria-hidden="true"></span>

  <div class="identity-control">
    @if (isAnonymousOnSavedBlob()) {
      <span class="title-display" [class.untitled]="!loadedBlobTitle()">
        {{ loadedBlobTitle() || untitledLabel }}
      </span>
    } @else if (isSignedIn()) {
      <input
        class="title-input title-field"
        type="text"
        [value]="title()"
        i18n-placeholder="@@toolbar.title.placeholder"
        placeholder="Add a title..."
        aria-label="Blob title"
        i18n-aria-label="@@toolbar.title.aria"
        maxlength="200"
        (input)="onTitleInput($event)"
        (keydown)="onTitleKeydown($event)"
      />
      <button
        type="button"
        mat-icon-button
        class="title-suggest-wand"
        [matTooltip]="wandTooltip"
        [attr.aria-label]="wandAriaLabel"
        [matMenuTriggerFor]="suggestionsMenu"
        [disabled]="!wandEnabled()"
        (click)="onWandClick()"
      >
        <jj-icon name="wand" />
      </button>
      <mat-menu #suggestionsMenu="matMenu" class="jj-menu">
        @for (candidate of suggestedTitles(); track candidate.value) {
          <button mat-menu-item type="button" (click)="onSuggestionSelected(candidate)">
            <span>{{ candidate.value }}</span>
          </button>
        }
      </mat-menu>
    }

    <div [class]="'state-pill ' + pillStateClass()" role="status" aria-live="polite">
      @if (isCta()) {
        <button
          mat-flat-button
          class="pill-cta"
          type="button"
          i18n-aria-label="@@toolbar.signInToSaveTooltip"
          aria-label="Sign in to save and share"
          (click)="signInRequested.emit()"
        >
          <span class="pill-text-full" i18n="@@toolbar.state.signInToSave">Sign in to save</span>
          <span class="pill-text-compact" i18n="@@toolbar.state.signInToSaveCompact">Sign in</span>
        </button>
      } @else {
        <span class="pill-text-full">{{ pillTextFull() }}</span>
        <span class="pill-text-compact">{{ pillTextCompact() }}</span>
      }
    </div>

    @if (showSaveBlock()) {
      <button
        type="button"
        mat-flat-button
        class="save-button"
        color="primary"
        [matTooltip]="saveTooltip()"
        [disabled]="saveDisabled()"
        (click)="onSaveClick()"
      >
        <jj-icon name="save" />
        <span>{{ saveButtonLabel() }}</span>
      </button>

      @if (showOverflowMenu()) {
        <button
          type="button"
          mat-icon-button
          [matMenuTriggerFor]="blobMenu"
          matTooltip="More actions"
          i18n-matTooltip="@@toolbar.overflow.tooltip"
          aria-label="More actions for this blob"
          i18n-aria-label="@@toolbar.overflow.aria"
        >
          <jj-icon name="more-vert" />
        </button>
        <mat-menu #blobMenu="matMenu" class="jj-menu">
          @if (isFileBacked()) {
            <button mat-menu-item type="button" (click)="onSaveAsNewFileClick()">
              <jj-icon name="save" />
              <span i18n="@@toolbar.overflow.saveAsNewFile">Save as new file...</span>
            </button>
            <ng-container *jjSignedIn>
              <button mat-menu-item type="button" (click)="onSaveAsBlobClick()">
                <jj-icon name="link" />
                <span i18n="@@toolbar.overflow.saveAsBlob">Save as blob...</span>
              </button>
            </ng-container>
          } @else {
            <button mat-menu-item type="button" (click)="onCopyShareLinkClick()">
              <jj-icon name="link" />
              <span i18n="@@toolbar.overflow.copyShareLink">Copy share link</span>
            </button>
            <button mat-menu-item type="button" (click)="onDeleteBlobClick()">
              <jj-icon name="trash" />
              <span i18n="@@toolbar.overflow.delete">Delete this blob</span>
            </button>
          }
        </mat-menu>
      }
    }
  </div>

  <span class="spacer"></span>

  <button
    type="button"
    mat-icon-button
    [matTooltip]="selectionSyncTooltip()"
    [attr.aria-label]="selectionSyncAriaLabel"
    [attr.aria-pressed]="selectionSyncEnabled()"
    (click)="toggleSelectionSync.emit()"
  >
    <jj-icon [name]="selectionSyncIcon()" />
  </button>

  <mat-button-toggle-group
    class="pane-layout-group"
    [value]="paneLayout()"
    (change)="onPaneLayoutChange($any($event.value))"
    [attr.aria-label]="paneLayoutGroupAriaLabel"
  >
    <mat-button-toggle
      value="editor-only"
      [matTooltip]="paneLayoutEditorOnlyLabel"
      [aria-label]="paneLayoutEditorOnlyLabel"
    >
      <jj-icon name="pane-left-only" />
    </mat-button-toggle>
    <mat-button-toggle
      value="both-horizontal"
      [matTooltip]="paneLayoutBothHorizontalLabel"
      [aria-label]="paneLayoutBothHorizontalLabel"
    >
      <jj-icon name="pane-both" />
    </mat-button-toggle>
    <mat-button-toggle
      value="both-vertical"
      [matTooltip]="paneLayoutBothVerticalLabel"
      [aria-label]="paneLayoutBothVerticalLabel"
    >
      <jj-icon name="pane-stacked" />
    </mat-button-toggle>
    <mat-button-toggle
      value="tree-only"
      [matTooltip]="paneLayoutTreeOnlyLabel"
      [aria-label]="paneLayoutTreeOnlyLabel"
    >
      <jj-icon name="pane-right-only" />
    </mat-button-toggle>
  </mat-button-toggle-group>

  <jj-toolbar-beacon-pills [beaconIndex]="beaconIndex()" />

  <span class="divider" aria-hidden="true"></span>

  <button
    type="button"
    mat-icon-button
    [matTooltip]="themeToggleLabel()"
    [attr.aria-label]="themeToggleLabel()"
    (click)="toggleTheme.emit()"
  >
    <jj-icon [name]="themeIcon()" />
  </button>
</div>
